import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, Sparkles, Loader2, MessageSquareText, Search, X, LayoutTemplate } from "lucide-react";
import { api } from "@/lib/api";
import AppBar from "@/components/ui-v2/AppBar";

/**
 * /dev-os/new — single-page wizard. Sections inline (not steps) so the user
 * can fill what they know and submit. Submission calls the Product CEO agent.
 */
export default function NewProject() {
        const nav = useNavigate();
        const [submitting, setSubmitting] = useState(false);
        const [importing, setImporting] = useState(false);
        const [pickerOpen, setPickerOpen] = useState(false);
        const [templateOpen, setTemplateOpen] = useState(false);
        const [templateId, setTemplateId] = useState(null);
        const [templateName, setTemplateName] = useState("");
        const [relatedChatId, setRelatedChatId] = useState(null);
        const [importedFrom, setImportedFrom] = useState("");
        const [form, setForm] = useState({
                name: "",
                description: "",
                target_users: "",
                problem: "",
                // business model
                pricing: "subscription",
                price_point: "",
                // requirements
                must_have: "",
                nice_to_have: "",
                compliance: "",
        });

        const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

        async function submit(e) {
                e.preventDefault();
                if (!form.name.trim()) {
                        toast.error("Please name the project");
                        return;
                }
                if (!form.problem.trim() && !form.description.trim()) {
                        toast.error("Tell the Product CEO what problem you're solving");
                        return;
                }
                setSubmitting(true);
                try {
                        const { data } = await api.post("/dev-projects", {
                                name: form.name.trim(),
                                description: form.description.trim(),
                                target_users: form.target_users.trim(),
                                problem: form.problem.trim(),
                                source: templateId ? "template" : relatedChatId ? "chat" : "manual",
                                template_id: templateId,
                                related_chat_id: relatedChatId,
                                business_model: {
                                        pricing: form.pricing,
                                        price_point: form.price_point.trim(),
                                },
                                requirements: {
                                        must_have: form.must_have.trim(),
                                        nice_to_have: form.nice_to_have.trim(),
                                        compliance: form.compliance.trim(),
                                },
                        });
                        toast.success("Plan generated by Product CEO");
                        nav(`/dev-os/projects/${data.id}`);
                } catch (err) {
                        if (!err.isCreditLimit) toast.error(err?.response?.data?.detail || "Could not create project");
                        setSubmitting(false);
                }
        }

        async function handleImport(chat) {
                setImporting(true);
                try {
                        const { data: msgs } = await api.get(`/chats/${chat.id}/messages?limit=40`);
                        const lines = (msgs || [])
                                .filter((m) => m.body && !m.deleted_at && m.message_type !== "ai-system")
                                .slice(-30)
                                .map((m) => m.body.replace(/\s+/g, " ").trim())
                                .filter((s) => s.length > 0);
                        if (lines.length === 0) {
                                toast.error("No messages in that chat yet");
                                return;
                        }
                        const blob = lines.join("\n\n");
                        setForm((f) => ({
                                ...f,
                                name: f.name || chat.name || "Imported project",
                                description: f.description || (chat.description || `Imported from #${chat.name}`),
                                problem: f.problem ? `${f.problem}\n\n---\n\n${blob}` : blob,
                        }));
                        setRelatedChatId(chat.id);
                        setImportedFrom(chat.name || "chat");
                        setPickerOpen(false);
                        toast.success(`Imported ${lines.length} messages from #${chat.name || "chat"}`);
                } catch (err) {
                        toast.error(err?.response?.data?.detail || "Could not import chat");
                } finally {
                        setImporting(false);
                }
        }

        return (
                <div className="min-h-[100dvh] bg-bg pb-24 md:pb-8 text-ink">
                        <AppBar
                                left={
                                        <Link to="/dev-os" className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-white/5" data-testid="new-project-back">
                                                <ChevronLeft className="w-5 h-5" />
                                        </Link>
                                }
                                center={<div className="text-[15px] font-semibold">New Project</div>}
                                right={null}
                        />

                        <form onSubmit={submit} className="px-4 md:px-5 pt-4 space-y-5 max-w-2xl mx-auto" data-testid="new-project-form">
                                <Link
                                        to="/dev-os/simple-builder"
                                        data-testid="np-simple-builder-banner"
                                        className="rounded-2xl bg-amber-400/[0.06] border border-amber-400/25 p-4 flex items-center gap-3 hover:border-amber-400/50 transition-colors"
                                >
                                        <Sparkles className="w-5 h-5 text-amber-300 shrink-0" />
                                        <div className="flex-1 text-[13px] text-ink leading-snug">
                                                <b>Prefer a guided start?</b> The Simple App Builder asks 3 plain-English questions —
                                                or lets you start from a working template.
                                        </div>
                                        <span className="text-[12px] text-amber-200 font-semibold shrink-0">Try it →</span>
                                </Link>
                                <div className="rounded-2xl bg-ai-tint/60 border border-ai/20 p-4 flex items-start gap-3">
                                        <Sparkles className="w-5 h-5 text-ai shrink-0 mt-0.5" />
                                        <div className="text-[13px] text-ink leading-snug">
                                                The <b>Product CEO agent</b> (Claude Sonnet 4.5) will read your brief and generate a full MVP plan: modules, schema, backlog, QA + security checklists.
                                        </div>
                                </div>

                                {/* Starter shortcuts — import from chat or pick a template */}
                                {templateId ? (
                                        <div className="rounded-2xl bg-brand-tint/50 border border-brand/30 p-3 flex items-center gap-3" data-testid="np-template-banner">
                                                <LayoutTemplate className="w-5 h-5 text-brand shrink-0" />
                                                <div className="flex-1 min-w-0">
                                                        <div className="text-[13px] font-semibold text-brand">Template: {templateName}</div>
                                                        <div className="text-[11px] text-ink-dim mt-0.5">Edit fields below before submitting.</div>
                                                </div>
                                                <button
                                                        type="button"
                                                        data-testid="np-clear-template"
                                                        onClick={() => { setTemplateId(null); setTemplateName(""); }}
                                                        className="w-7 h-7 rounded-full hover:bg-white/5 flex items-center justify-center text-ink-mute"
                                                        aria-label="Clear template"
                                                >
                                                        <X className="w-4 h-4" />
                                                </button>
                                        </div>
                                ) : relatedChatId ? (
                                        <div className="rounded-2xl bg-tn-green/10 border border-tn-green/30 p-3 flex items-center gap-3" data-testid="np-imported-banner">
                                                <MessageSquareText className="w-5 h-5 text-tn-green shrink-0" />
                                                <div className="flex-1 min-w-0">
                                                        <div className="text-[13px] font-semibold text-tn-green">Imported from #{importedFrom}</div>
                                                        <div className="text-[11px] text-ink-dim mt-0.5">Edit fields below before submitting.</div>
                                                </div>
                                                <button
                                                        type="button"
                                                        data-testid="np-clear-import"
                                                        onClick={() => { setRelatedChatId(null); setImportedFrom(""); }}
                                                        className="w-7 h-7 rounded-full hover:bg-white/5 flex items-center justify-center text-ink-mute"
                                                        aria-label="Clear import"
                                                >
                                                        <X className="w-4 h-4" />
                                                </button>
                                        </div>
                                ) : (
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                                <button
                                                        type="button"
                                                        data-testid="np-pick-template"
                                                        onClick={() => setTemplateOpen(true)}
                                                        className="h-12 rounded-2xl bg-surface border border-hairline hover:bg-white/[0.04] flex items-center justify-center gap-2 text-[13px] font-semibold text-ink-dim transition-colors"
                                                >
                                                        <LayoutTemplate className="w-4 h-4" />
                                                        Choose a template
                                                </button>
                                                <button
                                                        type="button"
                                                        data-testid="np-import-chat"
                                                        onClick={() => setPickerOpen(true)}
                                                        className="h-12 rounded-2xl bg-surface border border-hairline hover:bg-white/[0.04] flex items-center justify-center gap-2 text-[13px] font-semibold text-ink-dim transition-colors"
                                                >
                                                        <MessageSquareText className="w-4 h-4" />
                                                        Import from chat
                                                </button>
                                        </div>
                                )}

                                <Section title="The basics">
                                        <Field label="Project name *">
                                                <input
                                                        data-testid="np-name"
                                                        value={form.name}
                                                        onChange={set("name")}
                                                        placeholder="e.g. Helpdesk for veterinary clinics"
                                                        className={inputCls}
                                                />
                                        </Field>
                                        <Field label="One-line description">
                                                <input
                                                        data-testid="np-description"
                                                        value={form.description}
                                                        onChange={set("description")}
                                                        placeholder="A WhatsApp-style triage app for vet receptionists"
                                                        className={inputCls}
                                                />
                                        </Field>
                                        <Field label="Who is this for?">
                                                <input
                                                        data-testid="np-users"
                                                        value={form.target_users}
                                                        onChange={set("target_users")}
                                                        placeholder="Independent veterinary clinics, 2-15 staff"
                                                        className={inputCls}
                                                />
                                        </Field>
                                </Section>

                                <Section title="The problem *">
                                        <Field label="What problem are you solving?">
                                                <textarea
                                                        data-testid="np-problem"
                                                        value={form.problem}
                                                        onChange={set("problem")}
                                                        placeholder="Vet receptionists spend hours on the phone scheduling pet visits, triaging urgent cases, and chasing follow-ups. We want to give them an AI-powered inbox that prioritises emergencies, books slots, and sends reminders."
                                                        rows={5}
                                                        className={`${inputCls} resize-none`}
                                                />
                                        </Field>
                                </Section>

                                <Section title="Business model">
                                        <div className="grid grid-cols-2 gap-3">
                                                <Field label="Pricing">
                                                        <select data-testid="np-pricing" value={form.pricing} onChange={set("pricing")} className={inputCls}>
                                                                <option value="subscription">Subscription</option>
                                                                <option value="usage">Usage-based</option>
                                                                <option value="freemium">Freemium</option>
                                                                <option value="one_time">One-time</option>
                                                                <option value="enterprise">Enterprise</option>
                                                        </select>
                                                </Field>
                                                <Field label="Price point">
                                                        <input
                                                                data-testid="np-price"
                                                                value={form.price_point}
                                                                onChange={set("price_point")}
                                                                placeholder="$49/mo per clinic"
                                                                className={inputCls}
                                                        />
                                                </Field>
                                        </div>
                                </Section>

                                <Section title="Requirements">
                                        <Field label="Must-have">
                                                <textarea
                                                        data-testid="np-must"
                                                        value={form.must_have}
                                                        onChange={set("must_have")}
                                                        rows={3}
                                                        placeholder="Triage queue, calendar sync, SMS reminders"
                                                        className={`${inputCls} resize-none`}
                                                />
                                        </Field>
                                        <Field label="Nice-to-have">
                                                <textarea
                                                        data-testid="np-nice"
                                                        value={form.nice_to_have}
                                                        onChange={set("nice_to_have")}
                                                        rows={2}
                                                        placeholder="Voice notes, multi-clinic dashboard"
                                                        className={`${inputCls} resize-none`}
                                                />
                                        </Field>
                                        <Field label="Compliance / constraints">
                                                <input
                                                        data-testid="np-compliance"
                                                        value={form.compliance}
                                                        onChange={set("compliance")}
                                                        placeholder="HIPAA-ish, must work offline on iPad"
                                                        className={inputCls}
                                                />
                                        </Field>
                                </Section>

                                <button
                                        type="submit"
                                        data-testid="np-submit"
                                        disabled={submitting}
                                        className="w-full h-14 rounded-2xl bg-brand text-black font-semibold flex items-center justify-center gap-2 disabled:opacity-60 hover:bg-brand-deep transition-colors"
                                >
                                        {submitting ? (
                                                <>
                                                        <Loader2 className="w-5 h-5 animate-spin" /> Product CEO is drafting…
                                                </>
                                        ) : (
                                                <>
                                                        <Sparkles className="w-5 h-5" /> Generate project plan
                                                </>
                                        )}
                                </button>
                                <div className="text-center text-[11px] text-ink-mute">
                                        ~25 credits · Claude Sonnet 4.5 · returns a full MVP blueprint
                                </div>
                        </form>
                        {pickerOpen && (
                                <ChatPicker
                                        importing={importing}
                                        onClose={() => setPickerOpen(false)}
                                        onPick={handleImport}
                                />
                        )}
                        {templateOpen && (
                                <TemplatePicker
                                        onClose={() => setTemplateOpen(false)}
                                        onPick={(t) => {
                                                const b = t.brief;
                                                setForm((f) => ({
                                                        ...f,
                                                        name: f.name || b.name || "",
                                                        description: f.description || b.description || "",
                                                        target_users: f.target_users || b.target_users || "",
                                                        problem: f.problem || b.problem || "",
                                                        pricing: b.business_model?.pricing || f.pricing,
                                                        price_point: f.price_point || b.business_model?.price_point || "",
                                                        must_have: f.must_have || b.requirements?.must_have || "",
                                                        nice_to_have: f.nice_to_have || b.requirements?.nice_to_have || "",
                                                        compliance: f.compliance || b.requirements?.compliance || "",
                                                }));
                                                setTemplateId(t.id);
                                                setTemplateName(t.name);
                                                setTemplateOpen(false);
                                                toast.success(`Loaded "${t.name}" template`);
                                        }}
                                />
                        )}
                </div>
        );
}

function TemplatePicker({ onClose, onPick }) {
        const [templates, setTemplates] = useState([]);
        const [loading, setLoading] = useState(true);
        const [picking, setPicking] = useState(null);

        useEffect(() => {
                api.get("/dev-os/templates")
                        .then((r) => setTemplates(r.data.templates || []))
                        .catch(() => toast.error("Could not load templates"))
                        .finally(() => setLoading(false));
        }, []);

        const choose = async (t) => {
                setPicking(t.id);
                try {
                        const { data } = await api.get(`/dev-os/templates/${t.id}`);
                        onPick(data);
                } catch {
                        toast.error("Could not load template");
                } finally {
                        setPicking(null);
                }
        };

        return (
                <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-6" onClick={onClose} data-testid="template-picker">
                        <div
                                onClick={(e) => e.stopPropagation()}
                                className="w-full md:max-w-xl max-h-[85vh] rounded-t-3xl md:rounded-3xl bg-surface border border-hairline flex flex-col"
                        >
                                <div className="p-4 border-b border-hairline flex items-center gap-2">
                                        <div className="w-9 h-9 rounded-xl bg-brand-tint text-brand flex items-center justify-center">
                                                <LayoutTemplate className="w-5 h-5" />
                                        </div>
                                        <div className="flex-1">
                                                <div className="text-[15px] font-bold">Pick a starter template</div>
                                                <div className="text-[12px] text-ink-dim">Pre-fills the brief so the Product CEO doesn&apos;t start from scratch.</div>
                                        </div>
                                        <button type="button" onClick={onClose} className="w-9 h-9 rounded-full hover:bg-white/5 flex items-center justify-center" data-testid="template-close">
                                                <X className="w-4 h-4" />
                                        </button>
                                </div>
                                <div className="flex-1 overflow-y-auto p-3 space-y-2">
                                        {loading ? (
                                                <div className="text-center text-ink-dim text-[13px] py-8">Loading templates…</div>
                                        ) : templates.length === 0 ? (
                                                <div className="text-center text-ink-dim text-[13px] py-8">No templates available.</div>
                                        ) : templates.map((t) => (
                                                <div
                                                        key={t.id}
                                                        role="button"
                                                        tabIndex={0}
                                                        data-testid={`template-${t.id}`}
                                                        onClick={() => !picking && choose(t)}
                                                        onKeyDown={(e) => e.key === "Enter" && !picking && choose(t)}
                                                        className={`w-full text-left p-3 rounded-2xl bg-bg hover:bg-white/[0.04] active:bg-white/[0.06] transition flex gap-3 items-start cursor-pointer ${picking ? "opacity-50 pointer-events-none" : ""}`}
                                                >
                                                        <div className="w-10 h-10 rounded-xl bg-brand-tint text-brand flex items-center justify-center font-bold text-[13px] shrink-0">
                                                                {t.name.charAt(0)}
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                                <div className="text-[14px] font-semibold">{t.name}</div>
                                                                <div className="text-[12px] text-ink-dim mt-0.5">{t.tagline}</div>
                                                                <div className="text-[11px] text-ink-mute mt-1">Best for: {t.best_for}</div>
                                                                {t.code_template && (
                                                                        <a
                                                                                href={`/api/dev-os/templates/${t.id}/demo/index.html`}
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                                data-testid={`template-demo-${t.id}`}
                                                                                onClick={(e) => e.stopPropagation()}
                                                                                className="inline-flex items-center gap-1 mt-2 text-[11px] px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-300 ring-1 ring-emerald-500/30 hover:bg-emerald-500/20 no-underline"
                                                                        >
                                                                                ▶ Try the live demo
                                                                        </a>
                                                                )}
                                                        </div>
                                                        {picking === t.id && <Loader2 className="w-4 h-4 animate-spin text-ink-mute" />}
                                                </div>
                                        ))}
                                </div>
                        </div>
                </div>
        );
}

function ChatPicker({ onClose, onPick, importing }) {
        const [chats, setChats] = useState([]);
        const [loading, setLoading] = useState(true);
        const [q, setQ] = useState("");

        useEffect(() => {
                api.get("/chats")
                        .then((r) => {
                                const list = (r.data || []).filter((c) => c.type !== "personal_ai");
                                setChats(list);
                        })
                        .catch(() => toast.error("Could not load chats"))
                        .finally(() => setLoading(false));
        }, []);

        const filtered = chats.filter((c) =>
                (c.name || "").toLowerCase().includes(q.toLowerCase()),
        );

        return (
                <div
                        className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-6"
                        onClick={onClose}
                        data-testid="chat-picker"
                >
                        <div
                                onClick={(e) => e.stopPropagation()}
                                className="w-full md:max-w-lg max-h-[80vh] rounded-t-3xl md:rounded-3xl bg-surface border border-hairline flex flex-col"
                        >
                                <div className="p-4 border-b border-hairline">
                                        <div className="flex items-center gap-2 mb-3">
                                                <div className="w-9 h-9 rounded-xl bg-ai-tint text-ai flex items-center justify-center">
                                                        <MessageSquareText className="w-5 h-5" />
                                                </div>
                                                <div className="flex-1">
                                                        <div className="text-[15px] font-bold">Import from chat</div>
                                                        <div className="text-[12px] text-ink-dim">Pick a conversation — last 30 messages will pre-fill the brief.</div>
                                                </div>
                                                <button type="button" onClick={onClose} className="w-9 h-9 rounded-full hover:bg-white/5 flex items-center justify-center" data-testid="picker-close">
                                                        <X className="w-4 h-4" />
                                                </button>
                                        </div>
                                        <div className="relative">
                                                <Search className="w-4 h-4 text-ink-mute absolute left-3 top-1/2 -translate-y-1/2" />
                                                <input
                                                        data-testid="picker-search"
                                                        value={q}
                                                        onChange={(e) => setQ(e.target.value)}
                                                        placeholder="Search chats…"
                                                        className="w-full h-10 pl-9 pr-3 rounded-xl bg-bg border border-hairline text-[13px] focus:outline-none focus:ring-2 focus:ring-brand/40"
                                                />
                                        </div>
                                </div>
                                <div className="flex-1 overflow-y-auto p-2">
                                        {loading ? (
                                                <div className="text-center text-ink-dim text-[13px] py-8">Loading…</div>
                                        ) : filtered.length === 0 ? (
                                                <div className="text-center text-ink-dim text-[13px] py-8">No chats found.</div>
                                        ) : (
                                                filtered.map((c) => (
                                                        <button
                                                                key={c.id}
                                                                type="button"
                                                                disabled={importing}
                                                                data-testid={`picker-chat-${c.id}`}
                                                                onClick={() => onPick(c)}
                                                                className="w-full text-left px-3 py-3 rounded-xl hover:bg-white/5 active:bg-white/10 disabled:opacity-50 transition-colors flex items-center gap-3"
                                                        >
                                                                <div className="w-9 h-9 rounded-xl bg-brand-tint text-brand flex items-center justify-center font-bold text-[13px]">
                                                                        {(c.name || "?").charAt(0).toUpperCase()}
                                                                </div>
                                                                <div className="flex-1 min-w-0">
                                                                        <div className="text-[13px] font-semibold truncate">{c.name || "Untitled"}</div>
                                                                        <div className="text-[11px] text-ink-mute truncate">{c.type} · {(c.member_ids || []).length} members</div>
                                                                </div>
                                                                {importing && <Loader2 className="w-4 h-4 animate-spin text-ink-mute" />}
                                                        </button>
                                                ))
                                        )}
                                </div>
                        </div>
                </div>
        );
}

const inputCls =
        "w-full h-11 px-3.5 rounded-xl bg-surface border border-hairline text-ink text-[14px] placeholder:text-ink-mute focus:outline-none focus:ring-2 focus:ring-brand/40 focus:border-brand";

function Section({ title, children }) {
        return (
                <div>
                        <div className="text-[12px] font-semibold uppercase tracking-wider text-ink-mute mb-2 px-1">{title}</div>
                        <div className="space-y-3 rounded-2xl bg-surface/40 p-4 border border-hairline">{children}</div>
                </div>
        );
}

function Field({ label, children }) {
        return (
                <label className="block">
                        <div className="text-[12px] text-ink-dim mb-1.5">{label}</div>
                        {children}
                </label>
        );
}
